import { useEffect, useState } from "react";
import { Send, Loader2, CheckCircle2, AlertTriangle, History, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import api from "@/lib/api";

const PRESET_TEMPLATES = [
  {
    id: "ads_live",
    label: "Google Ads Push — now LIVE",
    subject: "Push Google Ads campaigns live from RankPilot — it's ready",
    html: `<p>Big news — we just got Google Ads API <strong>Basic Access</strong> approved.</p>
<p>That means you can now generate AI-powered campaigns inside RankPilot and push them live into your Google Ads account with a single click. No more copy/paste, no more rebuilding ads manually.</p>
<p><strong>What's new:</strong></p>
<ul>
  <li>One-click "Push live" → campaign created in your Google Ads account (paused for review)</li>
  <li>AI-generated Responsive Search Ads (15 headlines, 4 descriptions)</li>
  <li>Keyword groups + negative keyword lists auto-populated</li>
  <li>Daily performance sync back to your RankPilot dashboard</li>
</ul>
<p style="margin-top:24px;"><a href="https://myrankpilotai.com/google-ads" style="display:inline-block;background:#0055FF;color:#fff;text-decoration:none;padding:12px 24px;border-radius:4px;font-weight:600;">Push your first campaign live →</a></p>
<p style="margin-top:24px;color:#888;font-size:13px;">Already connected? Head to <a href="https://myrankpilotai.com/google-ads" style="color:#3377FF;">/google-ads</a>. Not connected yet? Go to <a href="https://myrankpilotai.com/integrations" style="color:#3377FF;">/integrations</a> and link your Google Ads account in 30 seconds.</p>`,
  },
  {
    id: "trial_reminder",
    label: "Trial about to expire",
    subject: "Your RankPilot trial expires soon — keep your rankings climbing",
    html: `<p>Your free trial is wrapping up in a few days. Don't let your SEO momentum slow down.</p>
<p>Upgrade now and keep all your projects, schedules and AI usage running without interruption.</p>
<p style="margin-top:24px;"><a href="https://myrankpilotai.com/upgrade" style="display:inline-block;background:#0055FF;color:#fff;text-decoration:none;padding:12px 24px;border-radius:4px;font-weight:600;">Choose a plan →</a></p>`,
  },
  {
    id: "feature_update",
    label: "Generic feature update",
    subject: "New in RankPilot AI — [headline]",
    html: `<p>Hey — quick note about what we shipped this week.</p>
<p><strong>What's new:</strong></p>
<ul>
  <li>[feature 1]</li>
  <li>[feature 2]</li>
  <li>[feature 3]</li>
</ul>
<p style="margin-top:24px;"><a href="https://myrankpilotai.com/dashboard" style="display:inline-block;background:#0055FF;color:#fff;text-decoration:none;padding:12px 24px;border-radius:4px;font-weight:600;">Open dashboard →</a></p>`,
  },
];

export default function AdminBroadcastTab() {
  const [subject, setSubject] = useState("");
  const [html, setHtml] = useState("");
  const [audience, setAudience] = useState("all");
  const [counts, setCounts] = useState({});
  const [history, setHistory] = useState([]);
  const [busy, setBusy] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  const loadMeta = async () => {
    try {
      const [c, h] = await Promise.all([
        api.get("/admin/audience-counts"),
        api.get("/admin/broadcast/history"),
      ]);
      setCounts(c.data || {});
      setHistory(h.data?.items || []);
    } catch {
      /* silent */
    }
  };

  useEffect(() => {
    loadMeta();
  }, []);

  const applyTemplate = (id) => {
    const t = PRESET_TEMPLATES.find((x) => x.id === id);
    if (!t) return;
    setSubject(t.subject);
    setHtml(t.html);
  };

  const send = async (testOnly) => {
    if (!subject.trim() || !html.trim()) {
      toast.error("Subject and body are required.");
      return;
    }
    if (!testOnly) {
      const size = counts[audience] || 0;
      if (size === 0) {
        toast.error(`No users match the "${audience}" audience.`);
        return;
      }
      if (!window.confirm(`Send to ${size} recipients in the "${audience}" audience?`)) return;
    }
    setBusy(true);
    setLastResult(null);
    try {
      const { data } = await api.post("/admin/broadcast", {
        subject: subject.trim(),
        html: html.trim(),
        audience,
        test_only: testOnly,
      });
      setLastResult(data);
      toast.success(testOnly ? "Test email sent to you" : `Sent to ${data.sent} recipients`);
      await loadMeta();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-broadcast">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="audience-counts">
        {["all", "trial", "paying", "free", "unverified"].map((a) => (
          <button
            key={a}
            onClick={() => setAudience(a)}
            className={`border rounded-sm p-3 text-left transition-colors ${
              audience === a
                ? "border-[#F59E0B] bg-[#F59E0B]/10"
                : "border-zinc-800 bg-zinc-950/60 hover:border-zinc-700"
            }`}
            data-testid={`audience-${a}`}
          >
            <div className="text-xs uppercase tracking-wider text-zinc-500">{a}</div>
            <div className="font-display text-2xl text-zinc-100 mt-1">{counts[a] ?? "—"}</div>
          </button>
        ))}
      </div>

      <div className="border border-zinc-800 bg-zinc-950/60 rounded-sm p-6 space-y-5">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-2">
            <Send className="w-5 h-5 text-[#F59E0B]" />
            <h3 className="font-display text-lg font-medium">Compose broadcast</h3>
          </div>
          <div className="flex items-center gap-2">
            <Label className="text-xs text-zinc-500">Template</Label>
            <Select onValueChange={applyTemplate}>
              <SelectTrigger className="w-64 bg-zinc-950 border-zinc-800 rounded-sm h-9 text-sm" data-testid="template-select">
                <SelectValue placeholder="Apply a template..." />
              </SelectTrigger>
              <SelectContent className="bg-zinc-950 border-zinc-800">
                {PRESET_TEMPLATES.map((t) => (
                  <SelectItem key={t.id} value={t.id} data-testid={`template-${t.id}`}>{t.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-4">
          <div className="md:col-span-2">
            <Label className="text-zinc-300 text-sm">Subject</Label>
            <Input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="What's the email about?"
              className="mt-2 bg-zinc-950 border-zinc-800 rounded-sm"
              data-testid="broadcast-subject"
            />
          </div>
          <div>
            <Label className="text-zinc-300 text-sm">Audience</Label>
            <Select value={audience} onValueChange={setAudience}>
              <SelectTrigger className="mt-2 bg-zinc-950 border-zinc-800 rounded-sm" data-testid="broadcast-audience">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-zinc-950 border-zinc-800">
                <SelectItem value="all" data-testid="audience-opt-all">All users ({counts.all ?? "?"})</SelectItem>
                <SelectItem value="trial" data-testid="audience-opt-trial">Trial users ({counts.trial ?? "?"})</SelectItem>
                <SelectItem value="paying" data-testid="audience-opt-paying">Paying customers ({counts.paying ?? "?"})</SelectItem>
                <SelectItem value="free" data-testid="audience-opt-free">Free users ({counts.free ?? "?"})</SelectItem>
                <SelectItem value="unverified" data-testid="audience-opt-unverified">Unverified emails ({counts.unverified ?? "?"})</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div>
          <Label className="text-zinc-300 text-sm">Body (HTML allowed)</Label>
          <Textarea
            value={html}
            onChange={(e) => setHtml(e.target.value)}
            rows={14}
            placeholder="<p>Hi there — ...</p>"
            className="mt-2 bg-zinc-950 border-zinc-800 rounded-sm font-mono text-sm resize-none"
            data-testid="broadcast-body"
          />
          <p className="text-xs text-zinc-600 mt-2 flex items-center gap-1.5">
            <AlertTriangle className="w-3 h-3" />
            Your HTML is wrapped in a branded dark-mode email shell. You don't need to add headers/footers.
          </p>
        </div>

        <div className="flex flex-wrap gap-3 pt-2">
          <Button
            onClick={() => send(true)}
            disabled={busy}
            variant="outline"
            className="bg-zinc-950 border-zinc-800 hover:bg-zinc-900 rounded-sm"
            data-testid="send-test"
          >
            {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
            Send test to me
          </Button>
          <Button
            onClick={() => send(false)}
            disabled={busy}
            className="bg-[#F59E0B] hover:bg-[#d97706] text-black rounded-sm font-medium"
            data-testid="send-broadcast"
          >
            {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
            Send to {counts[audience] || 0} recipients
          </Button>
        </div>

        {lastResult && (
          <div className="border border-zinc-800 bg-zinc-950/80 rounded-sm p-4" data-testid="last-result">
            <div className="flex items-center gap-2 text-sm">
              <CheckCircle2 className="w-4 h-4 text-[#10B981]" />
              <span className="text-zinc-300">
                {lastResult.test_only
                  ? "Test email dispatched"
                  : `Broadcast complete — ${lastResult.sent} sent, ${lastResult.failed} failed (${lastResult.audience_size} in audience)`}
              </span>
            </div>
          </div>
        )}
      </div>

      <div className="border border-zinc-800 bg-zinc-950/60 rounded-sm p-6">
        <div className="flex items-center gap-2 mb-4">
          <History className="w-4 h-4 text-zinc-500" />
          <h3 className="font-display text-base font-medium">Broadcast history</h3>
        </div>
        {history.length === 0 ? (
          <div className="text-sm text-zinc-500 py-6 text-center">No broadcasts sent yet.</div>
        ) : (
          <div className="space-y-2">
            {history.map((h, i) => (
              <div key={i} className="flex items-center justify-between gap-4 py-2 border-b border-zinc-900 last:border-0" data-testid={`broadcast-history-${i}`}>
                <div className="min-w-0 flex-1">
                  <div className="text-sm text-zinc-200 truncate font-medium">{h.subject}</div>
                  <div className="text-xs text-zinc-600 mt-0.5">
                    {h.created_at?.slice(0, 16).replace("T", " ")} · {h.sent_by}
                  </div>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <Badge variant="outline" className="rounded-sm border-zinc-800 text-xs">
                    <Users className="w-3 h-3 mr-1" />{h.audience}
                  </Badge>
                  <span className="text-xs text-zinc-500">
                    {h.test_only ? "TEST" : `${h.sent_count}/${h.audience_size}`}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
